import React from "react";
import { PlusCircle, Trash2, GripVertical } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { FormSection } from "../types/formTypes";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	DragEndEvent,
} from "@dnd-kit/core";
import {
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableItem, DragHandle } from "./ui/sortable-item";

interface FormBuilderSidebarProps {
	sections: FormSection[];
	activeSection: string;
	onSelectSection: (sectionId: string) => void;
	onAddSection: () => void;
	onDeleteSection: (sectionId: string) => void;
	onReorderSections: (result: { fromIndex: number; toIndex: number }) => void;
}

const FormBuilderSidebar: React.FC<FormBuilderSidebarProps> = ({
	sections,
	activeSection,
	onSelectSection,
	onAddSection,
	onDeleteSection,
	onReorderSections,
}) => {
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8, // Increased distance to avoid accidental drags
				tolerance: 5, // Add tolerance for slight movements
				delay: 150, // Short delay before activating drag
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (over && active.id !== over.id) {
			const oldIndex = sections.findIndex(
				(section) => section.id === active.id
			);
			const newIndex = sections.findIndex(
				(section) => section.id === over.id
			);

			if (oldIndex !== -1 && newIndex !== -1) {
				onReorderSections({
					fromIndex: oldIndex,
					toIndex: newIndex,
				});
			}
		}
	};

	return (
		<div className="w-full bg-white p-4 rounded-lg border border-gray-200 flex flex-col max-h-[calc(100vh-12rem)]">
			<div className="flex justify-between items-center mb-4">
				<h3 className="font-medium text-gray-900">Abschnitte</h3>
				<Button
					size="sm"
					variant="ghost"
					onClick={onAddSection}
					className="h-8 w-8 p-0"
				>
					<PlusCircle className="h-5 w-5" />
					<span className="sr-only">Abschnitt hinzufügen</span>
				</Button>
			</div>

			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragEnd={handleDragEnd}
			>
				<SortableContext
					items={sections.map((section) => section.id)}
					strategy={verticalListSortingStrategy}
				>
					<div className="space-y-2 overflow-y-auto flex-grow">
						{sections.map((section, index) => (
							<SortableItem
								key={section.id}
								id={section.id}
								handleSelector=".drag-handle"
							>
								<div
									className={`flex items-center justify-between p-2 rounded-md cursor-pointer hover:bg-gray-100 ${
										activeSection === section.id
											? "bg-gray-100 border-l-4 border-primary"
											: ""
									}`}
									onClick={() => onSelectSection(section.id)}
								>
									<div className="flex items-center gap-2 w-full overflow-hidden">
										<DragHandle>
											<GripVertical className="h-4 w-4 text-gray-400" />
										</DragHandle>
										<span className="truncate">
											{section.title ||
												`Section ${index + 1}`}
										</span>
									</div>
									<Button
										size="sm"
										variant="ghost"
										onClick={(e) => {
											e.stopPropagation();
											onDeleteSection(section.id);
										}}
										className="h-6 w-6 p-0 opacity-70 hover:opacity-100"
									>
										<Trash2 className="h-4 w-4" />
										<span className="sr-only">
											Abschnitt löschen
										</span>
									</Button>
								</div>
							</SortableItem>
						))}
					</div>
				</SortableContext>
			</DndContext>

			<Button
				variant="outline"
				size="sm"
				onClick={onAddSection}
				className="w-full mt-4 gap-2"
			>
				<PlusCircle className="h-4 w-4" />
				Abschnitt hinzufügen
			</Button>
		</div>
	);
};

export default FormBuilderSidebar;
